//import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { formatMoney } from "../lib/money.js";

// Definición de colores Fysaro para el PDF
const COLORS = {
  primary: "#5BAA9D", // El Cyan de Fysaro
  dark: "#224D46", // Slate-900 para textos principales
  muted: "#64748b", // Slate-500 para secundarios
  bg: "#F4EFE7", // Slate-50 para fondos de tabla
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: COLORS.dark,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 40,
    borderBottom: 2,
    borderBottomColor: COLORS.primary,
    paddingBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: COLORS.primary,
    letterSpacing: -1,
  },
  section: { marginBottom: 20 },
  label: {
    fontSize: 8,
    color: "#666",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#EEE",
    paddingVertical: 8,
    alignItems: "center",
  },
  tableHeader: {
    backgroundColor: COLORS.dark,
    color: "#FFF",
    padding: 8,
    flexDirection: "row",
    borderRadius: 4,
    marginBottom: 4,
  },
  col1: { flex: 4 }, // Descripción
  col2: { flex: 1, textAlign: "center" }, // Cantidad
  col3: { flex: 1, textAlign: "right" }, // Precio
  totalSection: { marginTop: 30, alignItems: "flex-end" },
  totalBox: {
    width: 150,
    marginTop: 23,
    padding: 15,
    backgroundColor: COLORS.bg,
    borderRadius: 8,
    borderRight: 4,
    borderRightColor: COLORS.primary,
  },

  // Marca de agua para BORRADOR
  watermark: {
    position: "absolute",
    top: "40%",
    left: "20%",
    fontSize: 60,
    color: "rgba(239, 68, 68, 0.1)", // Rojo muy suave
    transform: "rotate(-45deg)",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
});

export const createInvoicePDF = ({
  invoice,
  clinic,
  t,
}: {
  invoice: any;
  clinic: any;
  t: (key: string, vars?: any) => string;
}) => {
  const issuer = invoice.issuerSnapshot ?? clinic;
  const locale = invoice.invoiceLocale ?? issuer.language ?? "es";
  const currency = invoice.currency ?? issuer.currency ?? "EUR";
  const taxName = issuer.taxName ?? issuer.businessConf?.taxName ?? "IVA";
  const taxDefault =
    issuer.taxDefault ?? issuer.businessConf?.taxDefault ?? 0;
  const formatAmount = (amount: number) => formatMoney(amount, currency, locale);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua condicional */}
        {invoice.status === "DRAFT" && (
          <Text style={styles.watermark}>
            {t(`invoice:statusOptions.${invoice.status}`)}
          </Text>
        )}
        {invoice.status === "CANCELLED" && (
          <Text style={styles.watermark}>
            {t(`invoice:statusOptions.${invoice.status}`)}
          </Text>
        )}

        {/* ENCABEZADO Fysaro */}
        <View style={styles.header}>
          <View>
            {/* Nombre de la Clínica con estilo de marca */}
            <Text
              style={{
                fontSize: 24,
                fontWeight: "black",
                color: COLORS.primary,
                letterSpacing: -1,
              }}
            >
              {issuer.name.toUpperCase()}
            </Text>
            <Text
              style={{
                color: COLORS.muted,
                fontSize: 8,
                marginTop: 2,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {t("invoice:invoice.title")}
            </Text>
          </View>

          <View style={{ textAlign: "right" }}>
            <Text style={{ fontWeight: "bold", fontSize: 10 }}>
              {issuer.name}
            </Text>
            <Text>{issuer.taxIdType}: {issuer.taxId}</Text>
            <Text>{issuer.address}</Text>
            <Text>
              {issuer.zipCode}, {issuer.city}
            </Text>
            <Text>{issuer.phone}</Text>
          </View>
        </View>

        {/* SECCIÓN DATOS FACTURA Y PACIENTE (Grid 2 columnas) */}
        <View style={{ flexDirection: "row", marginBottom: 30, gap: 40 }}>
          <View
            style={{
              flex: 1,
              backgroundColor: COLORS.bg,
              padding: 12,
              borderRadius: 8,
            }}
          >
            <Text style={styles.label}>
              {t("invoice:invoice.patient_data")}
            </Text>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 2 }}>
              {invoice.patientName}
            </Text>
            <Text>{invoice.patientIdNumber}</Text>
            <Text>{invoice.patientEmail}</Text>
          </View>

          <View style={{ flex: 1, padding: 12 }}>
            <Text style={styles.label}>
              {t("invoice:invoice.invoice_data")}
            </Text>
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              <Text style={{ fontWeight: "bold" }}>
                {t("invoice:invoice.number")}:
              </Text>
              <Text>{invoice.invoiceNumber}</Text>
            </View>
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              <Text style={{ fontWeight: "bold" }}>
                {t("invoice:invoice.date")}:
              </Text>
              <Text>
                {dayjs(invoice.issuedAt ?? invoice.createdAt)
                  .locale(locale.split("-")[0])
                  .format("LL")}
              </Text>
            </View>
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              <Text style={{ fontWeight: "bold" }}>
                {t("invoice:invoice.status")}:
              </Text>
              <Text
                style={{
                  color: invoice.status === "PAID" ? COLORS.primary : "#000",
                }}
              >
                {t(`invoice:statusOptions.${invoice.status}`)}
              </Text>
            </View>
          </View>
        </View>

        {/* TABLA DE ITEMS */}
        <View style={styles.section}>
          <View style={[styles.row, styles.tableHeader]}>
            <Text style={styles.col1}>
              {t("invoice:invoice.line_description")}
            </Text>
            <Text style={styles.col2}>
              {t("invoice:invoice.line_quantity")}
            </Text>
            <Text style={styles.col3}>{t("invoice:invoice.line_total")}</Text>
          </View>
          {(invoice.items as {
            description: string;
            quantity: number;
            total: number;
          }[]).map((item, i) => (
            <View key={i} style={styles.row}>
              <Text style={styles.col1}>{item.description}</Text>
              <Text style={styles.col2}>{item.quantity}</Text>
              <Text style={styles.col3}>{formatAmount(item.total)}</Text>
            </View>
          ))}
     
        </View>

        {/* TOTALES */}
        <View style={styles.totalSection}>
          <View style={styles.totalBox}>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginBottom: 5,
              }}
            >
              <Text style={{ color: "#666" }}>
                {t("invoice:invoice.subtotal")}:
              </Text>
              <Text>{formatAmount(invoice.subtotal)}</Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginBottom: 5,
              }}
            >
              <Text style={{ color: "#666" }}>
                {taxName} {taxDefault}
                :
              </Text>
              <Text>{formatAmount(invoice.taxAmount)}</Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginTop: 5,
              }}
            >
              <Text style={{ fontWeight: "bold", fontSize: 14 }}>
                {t("invoice:invoice.total").toUpperCase()}:
              </Text>
              <Text style={{ fontWeight: "bold", fontSize: 14 }}>
                {formatAmount(invoice.total)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
